(ns app.front.view.group.admin.approver-groups
  "UI for selecting the active approver (stakeholder) groups for the
  municipaly organizational unit. (e.g., Windsor Building Control)"
  (:require [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.sortable-table :as sortable-table]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(def interceptors [(rf/inject-cofx :tr)])

(rf/reg-event-fx
  ::toast-success
  interceptors
  (fn [{tr :tr}]
    {:dispatch [:notifications/add
                :toast
                :success
                (tr [:approvals/saved])
                ""]}))

(rf/reg-sub
  ::group
  (fn [db]
    (get-in db [::entities/entity-db (::group-id db)])))

;; -----------------------------
;; Approver groups selection

(rc/reg-chain ::fetch-approver-groups
  (fn [_ [_ group-id]]
    {:comm/command {:command :group/approver-groups
                    :method  :get
                    :data    {:group-id group-id}}})
  (fn [_ [_ _ status body]]
    (when (:ok body)
      {:dispatch [::entities/merge-entity-db-for-all
                  {:approver-group? true}
                  status
                  (p/index-by :db/id (:groups body))]})))

(rf/reg-sub
  ::approver-groups
  :<- [::group]
  :<- [::entities/entities {:approver-group? true}]
  (fn [[group groups]]
    (let [selected (->> group :group/approver-groups set)]
      (map (fn [g]
             (-> g
                 (select-keys [:db/id :group/name :domain-name])
                 (p/assoc-if ::selected? (contains? selected (:db/id g)))))
           groups))))

(rf/reg-event-db
  ::approver-groups-state
  (fn [db [_ k v]]
    (assoc-in db [::approver-groups-state k] v)))

(rf/reg-sub
  ::approver-groups-state?
  (fn [db [_ k]]
    (boolean (get-in db [::approver-groups-state k]))))

(rc/reg-chain ::select-approver-groups
  (fn [{db :db} [_ checked? group-ids]]
    {:comm/command {:command :group/edit-approver-groups
                    :data    (assoc {:group-id (::group-id db)
                                     :add      []
                                     :remove   []}
                                    (if checked? :add :remove)
                                    group-ids)}})
  (fn [{db :db} [_ _ _ status body]]
    (let [updated (:group/approver-groups body)]
      {:dispatch-n [[::entities/apply-changes
                     status
                     {(::group-id db) {:group/approver-groups
                                       (if (empty? updated)
                                         [:dissoc]
                                         [:assoc updated])}}]
                    [::toast-success]]})))



;; -- Other events ------------------------------------------------------------------

(rf/reg-event-db ::init
  interceptors
  (fn [db [_ group-id]]
    (assoc db ::group-id group-id)))

;; ----------------------------
;; Approver groups selection
;; ----------------------------

(defn- approver-checkbox [{:keys [id label checked? on-change]}]
  [:span.relative
   [:input.bx--checkbox {:type       "checkbox"
                         :id         id
                         :aria-label label
                         :checked    checked?
                         :on-change  on-change}]
   [:label.bx--checkbox-label {:for id}]])

(defn approver-groups-form [tr group-name]
  (let [editing? (<sub [::approver-groups-state? :editing?])
        groups   (<sub [::approver-groups])
        groups?  (seq groups)
        edit-fn  #(>evt [::approver-groups-state :editing? (not editing?)])]
    [:<>
     [:div.px-8.flex.justify-between
      [:h1 (tr [:approvals/groups] [group-name])]
      (when groups?
        (cc/button {:kind       (if editing? :secondary :primary)
                    :class-name "align-middle"
                    :size       :small
                    :on-click   edit-fn}
                   (tr [(if editing? :admin/stop-editing :admin/edit)])))]
     [:section>fieldset
      (cond
        (not groups?)
        (tr [:approvals/no-groups])

        (and (not-any? ::selected? groups)
             (not editing?))
        [:div
         (tr [:approvals/none-selected-first])
         [:a {:href     "#"
              :on-click edit-fn} (tr [:user-management/edit])]
         (tr [:approvals/none-selected-second])]

        :else
        [sortable-table/table
         {:tr              tr
          :initial-sorting [:group/name :asc]
          :items           (cond->> groups
                             (not editing?) (filter ::selected?))
          :cols            (cond->> [[:approvals/name :group/name {:sorting true}]
                                     [:approvals/domain :domain-name {:sorting true}]]
                             editing?
                             (cons [[approver-checkbox
                                     {:id        "select-all-approver-groups"
                                      :label     (tr [:approvals/select-all])
                                      :checked?  (every? ::selected? groups)
                                      :on-change #(>evt [::select-approver-groups (.. % -target -checked) (map :db/id groups)])}]
                                    (fn [{gid :db/id gname :group/name checked? ::selected?}]
                                      [approver-checkbox
                                       {:id        (str "check-" gid)
                                        :label     gname
                                        :checked?  checked?
                                        :on-change #(>evt [::select-approver-groups (.. % -target -checked) [gid]])}])
                                    {:width "62px"}]))}])]]))

;; --------------------
;; Main view
;; --------------------


(defn view [match]
  (let [tr                      (<sub [:tr])
        {group-name :group/name
         :as        group}      (<sub [::group])]
    (when (group-model/application-or-upper-tier-authority? group)
      [:div.cloudpermit-page
       [ui/page-header (util/mmap match)
        [approver-groups-form tr group-name]]])))

(def route
  {:name        :view/group.admin.approver-groups
   :view        view
   :public?     false
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (>evt [::init (-> params :path :group-id)])
                                (>evt [::fetch-approver-groups (-> params :path :group-id)]))}]})
